import Phaser from 'phaser';
import { TILE_SIZE, CANVAS_WIDTH, CANVAS_HEIGHT, GAME_WIDTH, GAME_HEIGHT, MAP_COLS, MAP_ROWS } from '@/config/GameConfig';
import { EventBus, GameEvents } from '@/events/EventBus';
import { GOD } from '@/config/GodMode';

/**
 * SmartCamera — action-density-based camera during waves.
 *
 * Each frame, collects weighted "action points" from:
 *   - Alive enemies (weight 1.0)
 *   - Alive robots (weight 0.3)
 *   - Combat event hotspots with time-decay (kills, shots, base hits)
 *
 * Points in fog-of-war (unexplored) are excluded so the camera
 * only tracks visible action.
 *
 * Optional heatmap overlay renders accumulated density as a
 * color grid (blue → red) over the map when GOD.showHeatmap is on.
 */

interface ActionPoint {
  x: number;
  y: number;
  weight: number;
}

interface Hotspot {
  x: number;
  y: number;
  weight: number;
  /** Remaining lifetime in seconds */
  ttl: number;
}

/** Minimal entity interface — avoids coupling to Enemy/Robot classes */
interface Positionable {
  x: number;
  y: number;
  alive: boolean;
}

/** Enemy with body sprite for position */
interface EnemyLike {
  body: { x: number; y: number };
  alive: boolean;
}

/** Visibility checker callback — returns true if world position is revealed */
export type VisibilityChecker = (px: number, py: number) => boolean;

export interface CameraTarget {
  x: number;
  y: number;
  zoom: number;
}

// ── Tuning constants ──
const HOTSPOT_TTL = 2.0;          // seconds a combat event stays relevant
const HOTSPOT_WEIGHT = 3.0;       // initial weight of a combat event
const ENEMY_WEIGHT = 1.0;
const ROBOT_WEIGHT = 0.3;
const MIN_FRAME_SIZE = TILE_SIZE * 6;  // never zoom in tighter than 6 tiles
const FRAME_PADDING = TILE_SIZE * 2;   // padding around action bounding box

// ── Cluster detection ──
const CLUSTER_RADIUS = TILE_SIZE * 5;  // points within this distance belong to same cluster
const CLUSTER_SWITCH_THRESHOLD = 1.5;  // new cluster must be 1.5× heavier to steal focus
const CLUSTER_SWITCH_COOLDOWN = 2.0;   // seconds before camera can switch clusters
// CONTESTED_RATIO is now read from GOD.cameraContestedRatio (tunable via GodMode panel)

// ── Heatmap constants ──
const HEAT_CELL = 2;  // cells are 2×2 tiles for performance
const HEAT_DECAY = 0.92; // per-frame decay (smooth fade)

export class SmartCamera {
  private hotspots: Hotspot[] = [];

  // ── Cluster tracking state ──
  /** Index of the cluster the camera is currently following */
  private activeClusterIdx = -1;
  /** Cooldown timer — prevents rapid cluster switching */
  private switchCooldown = 0;

  // ── EMA smoothing on the raw target to absorb frame-to-frame jitter ──
  private smoothX = 0;
  private smoothY = 0;
  private smoothZoom = 1;
  private smoothInited = false;
  private static readonly EMA_SPEED = 1.5; // per second — lower = smoother

  // ── Heatmap state ──
  private heatCols = 0;
  private heatRows = 0;
  private heatGrid: number[] = []; // flat array [row * heatCols + col]
  private heatGfx: Phaser.GameObjects.Graphics | null = null;
  private heatScene: Phaser.Scene | null = null;

  // ── Event listeners (bound for cleanup) ──
  private onEnemyDestroyed = (e: { body?: { x: number; y: number } }) => {
    if (e.body) this.addHotspot(e.body.x, e.body.y, HOTSPOT_WEIGHT);
  };
  private onRobotFired = (data: { robot?: { x: number; y: number } }) => {
    if (data.robot) this.addHotspot(data.robot.x, data.robot.y, HOTSPOT_WEIGHT * 0.5);
  };
  private onEnemyFired = (data: { sx: number; sy: number }) => {
    this.addHotspot(data.sx, data.sy, HOTSPOT_WEIGHT * 0.4);
  };
  private onBaseDamaged = (data: { hp: number; x: number; y: number }) => {
    // Base under attack — high priority hotspot to pull camera toward the base
    this.addHotspot(data.x, data.y, HOTSPOT_WEIGHT * 1.5);
  };

  constructor() {
    EventBus.on(GameEvents.ENEMY_DESTROYED, this.onEnemyDestroyed);
    EventBus.on(GameEvents.ROBOT_FIRED, this.onRobotFired);
    EventBus.on(GameEvents.ENEMY_FIRED, this.onEnemyFired);
    EventBus.on(GameEvents.BASE_DAMAGED, this.onBaseDamaged);
  }

  /** Initialize heatmap grid + graphics (call once after scene create) */
  initHeatmap(scene: Phaser.Scene): void {
    this.heatScene = scene;
    this.heatCols = Math.ceil(MAP_COLS / HEAT_CELL);
    this.heatRows = Math.ceil(MAP_ROWS / HEAT_CELL);
    this.heatGrid = new Array(this.heatCols * this.heatRows).fill(0);
    this.heatGfx = scene.add.graphics().setDepth(4); // just below fog (depth 5)
    this.heatGfx.setVisible(false);
  }

  /** Add a temporary action hotspot at a position */
  private addHotspot(x: number, y: number, weight: number): void {
    this.hotspots.push({ x, y, weight, ttl: HOTSPOT_TTL });
  }

  /** Public API for ImpactTimeSystem — inject a high-priority hotspot to pull camera toward an impact */
  addImpactHotspot(x: number, y: number, weight: number): void {
    this.hotspots.push({ x, y, weight, ttl: HOTSPOT_TTL * 1.5 });
  }

  /** Decay and prune hotspots */
  private decayHotspots(dt: number): void {
    for (let i = this.hotspots.length - 1; i >= 0; i--) {
      this.hotspots[i].ttl -= dt;
      if (this.hotspots[i].ttl <= 0) {
        this.hotspots.splice(i, 1);
      }
    }
  }

  /**
   * Compute the ideal camera target based on current action density.
   * Call every frame during WAVE phase.
   * @param isVisible Optional fog checker — points in fog are excluded
   */
  computeTarget(
    enemies: EnemyLike[],
    robots: Positionable[],
    dt: number,
    isVisible?: VisibilityChecker,
  ): CameraTarget {
    this.decayHotspots(dt);

    // Collect all weighted action points (fog-filtered)
    const points: ActionPoint[] = [];

    for (const enemy of enemies) {
      if (!enemy.alive) continue;
      const ex = enemy.body.x;
      const ey = enemy.body.y;
      if (isVisible && !isVisible(ex, ey)) continue;
      points.push({ x: ex, y: ey, weight: ENEMY_WEIGHT });
    }

    for (const robot of robots) {
      if (!robot.alive) continue;
      // Robots are always on player side — always visible
      points.push({ x: robot.x, y: robot.y, weight: ROBOT_WEIGHT });
    }

    for (const hs of this.hotspots) {
      if (isVisible && !isVisible(hs.x, hs.y)) continue;
      // Weight decays linearly with remaining TTL
      const factor = hs.ttl / HOTSPOT_TTL;
      points.push({ x: hs.x, y: hs.y, weight: hs.weight * factor });
    }

    // Accumulate into heatmap grid
    this.accumulateHeat(points);

    // Fallback: if no action points, center on map
    if (points.length === 0) {
      this.activeClusterIdx = -1;
      return {
        x: GAME_WIDTH / 2,
        y: GAME_HEIGHT / 2,
        zoom: Math.min(CANVAS_WIDTH / GAME_WIDTH, CANVAS_HEIGHT / GAME_HEIGHT),
      };
    }

    // ── Cluster detection (greedy seed-expand) ──
    const clusters = this.buildClusters(points);
    this.switchCooldown = Math.max(0, this.switchCooldown - dt);

    // Determine which points the camera should frame
    const framePoints = this.selectFramePoints(clusters);

    // Weighted centroid of selected points
    let totalW = 0;
    let cx = 0;
    let cy = 0;
    for (const p of framePoints) {
      cx += p.x * p.weight;
      cy += p.y * p.weight;
      totalW += p.weight;
    }
    cx /= totalW;
    cy /= totalW;

    // Weighted spread
    let varX = 0;
    let varY = 0;
    for (const p of framePoints) {
      const dx = p.x - cx;
      const dy = p.y - cy;
      varX += p.weight * dx * dx;
      varY += p.weight * dy * dy;
    }
    varX /= totalW;
    varY /= totalW;

    // Use 2× std dev as the frame radius (captures ~95% of weighted area)
    const spreadX = Math.max(MIN_FRAME_SIZE / 2, Math.sqrt(varX) * 2) + FRAME_PADDING;
    const spreadY = Math.max(MIN_FRAME_SIZE / 2, Math.sqrt(varY) * 2) + FRAME_PADDING;

    const frameW = spreadX * 2;
    const frameH = spreadY * 2;

    const zoomX = CANVAS_WIDTH / frameW;
    const zoomY = CANVAS_HEIGHT / frameH;
    const minZoom = Math.min(CANVAS_WIDTH / GAME_WIDTH, CANVAS_HEIGHT / GAME_HEIGHT);
    const zoom = Math.max(minZoom, Math.min(4, Math.min(zoomX, zoomY))) * GOD.cameraZoomMult;

    // Soft-clamp: allow camera past map edges (shows black padding)
    // so action on borders can be centered, but keep at least half the
    // viewport overlapping the map so it doesn't drift into empty space.
    const clampedX = Math.max(0, Math.min(GAME_WIDTH, cx));
    const clampedY = Math.max(0, Math.min(GAME_HEIGHT, cy));

    // EMA smoothing — absorb rapid target changes before they reach the lerp
    if (!this.smoothInited) {
      this.smoothX = clampedX;
      this.smoothY = clampedY;
      this.smoothZoom = zoom;
      this.smoothInited = true;
    } else {
      const alpha = Math.min(1, SmartCamera.EMA_SPEED * dt);
      this.smoothX += (clampedX - this.smoothX) * alpha;
      this.smoothY += (clampedY - this.smoothY) * alpha;
      this.smoothZoom += (zoom - this.smoothZoom) * alpha;
    }

    return { x: this.smoothX, y: this.smoothY, zoom: this.smoothZoom };
  }

  /**
   * Greedy seed-expand clustering: pick the heaviest unassigned point as seed,
   * pull in all points within CLUSTER_RADIUS, repeat until none left.
   */
  private buildClusters(points: ActionPoint[]): ActionPoint[][] {
    const assigned = new Uint8Array(points.length);
    const clusters: ActionPoint[][] = [];
    const r2 = CLUSTER_RADIUS * CLUSTER_RADIUS;

    while (true) {
      // Find heaviest unassigned point as seed
      let seedIdx = -1;
      let seedW = -1;
      for (let i = 0; i < points.length; i++) {
        if (!assigned[i] && points[i].weight > seedW) {
          seedW = points[i].weight;
          seedIdx = i;
        }
      }
      if (seedIdx < 0) break;

      // Expand cluster from seed
      const cluster: ActionPoint[] = [];
      // Compute cluster centroid iteratively
      let cx = 0, cy = 0, tw = 0;
      const queue = [seedIdx];
      assigned[seedIdx] = 1;

      while (queue.length > 0) {
        const idx = queue.pop()!;
        const p = points[idx];
        cluster.push(p);
        cx = (cx * tw + p.x * p.weight) / (tw + p.weight);
        cy = (cy * tw + p.y * p.weight) / (tw + p.weight);
        tw += p.weight;

        // Pull in nearby unassigned points (compare to cluster centroid)
        for (let i = 0; i < points.length; i++) {
          if (assigned[i]) continue;
          const dx = points[i].x - cx;
          const dy = points[i].y - cy;
          if (dx * dx + dy * dy <= r2) {
            assigned[i] = 1;
            queue.push(i);
          }
        }
      }
      clusters.push(cluster);
    }
    return clusters;
  }

  /**
   * Decide which points the camera should frame.
   *
   * Strategy:
   *  - If one cluster clearly dominates → follow that cluster only (zoom in)
   *  - If multiple clusters are similarly heavy → merge them all and zoom out
   *    to show the full contested area (avoids rapid back-and-forth panning)
   *  - Cooldown prevents rapid switching between single-cluster targets
   */
  private selectFramePoints(clusters: ActionPoint[][]): ActionPoint[] {
    if (clusters.length <= 1) {
      this.activeClusterIdx = 0;
      return clusters[0];
    }

    // Compute total weight per cluster, sorted descending
    const indexed = clusters.map((c, i) => ({
      idx: i,
      weight: c.reduce((s, p) => s + p.weight, 0),
    }));
    indexed.sort((a, b) => b.weight - a.weight);

    const heaviest = indexed[0];
    const second = indexed[1];

    // Contested: 2nd cluster is at least CONTESTED_RATIO of the heaviest
    const isContested = second.weight >= heaviest.weight * GOD.cameraContestedRatio;

    if (isContested) {
      // Merge all clusters that are above the contested threshold and zoom out
      this.activeClusterIdx = -1; // no single cluster focus
      const merged: ActionPoint[] = [];
      const threshold = heaviest.weight * GOD.cameraContestedRatio;
      for (const entry of indexed) {
        if (entry.weight >= threshold) {
          for (const p of clusters[entry.idx]) merged.push(p);
        }
      }
      return merged;
    }

    // Single dominant cluster — apply hysteresis + cooldown
    // Stick with current cluster if cooldown active
    if (this.activeClusterIdx >= 0 && this.activeClusterIdx < clusters.length && this.switchCooldown > 0) {
      return clusters[this.activeClusterIdx];
    }

    // Switch only if new cluster is significantly heavier
    if (this.activeClusterIdx >= 0 && this.activeClusterIdx < clusters.length) {
      const currentW = clusters[this.activeClusterIdx].reduce((s, p) => s + p.weight, 0);
      if (heaviest.weight < currentW * CLUSTER_SWITCH_THRESHOLD) {
        return clusters[this.activeClusterIdx];
      }
    }

    // Switch to the heaviest cluster
    if (heaviest.idx !== this.activeClusterIdx) {
      this.switchCooldown = CLUSTER_SWITCH_COOLDOWN;
    }
    this.activeClusterIdx = heaviest.idx;
    return clusters[heaviest.idx];
  }

  // ── Heatmap ──

  /** Accumulate action points into the low-res heat grid and decay old values */
  private accumulateHeat(points: ActionPoint[]): void {
    if (!this.heatGfx) return;
    const cellPx = HEAT_CELL * TILE_SIZE;

    // Decay existing heat
    for (let i = 0; i < this.heatGrid.length; i++) {
      this.heatGrid[i] *= HEAT_DECAY;
    }

    // Accumulate new points
    for (const p of points) {
      const hc = Math.floor(p.x / cellPx);
      const hr = Math.floor(p.y / cellPx);
      if (hc < 0 || hc >= this.heatCols || hr < 0 || hr >= this.heatRows) continue;
      this.heatGrid[hr * this.heatCols + hc] += p.weight;
    }

    // Render heatmap if enabled
    if (GOD.showHeatmap) {
      this.heatGfx.setVisible(true);
      this.drawHeatmap(cellPx);
    } else {
      this.heatGfx.setVisible(false);
    }
  }

  /** Render the heat grid as colored rectangles */
  private drawHeatmap(cellPx: number): void {
    const gfx = this.heatGfx!;
    gfx.clear();

    // Find max heat for normalization
    let maxHeat = 0;
    for (let i = 0; i < this.heatGrid.length; i++) {
      if (this.heatGrid[i] > maxHeat) maxHeat = this.heatGrid[i];
    }
    if (maxHeat < 0.1) return; // nothing to draw

    for (let hr = 0; hr < this.heatRows; hr++) {
      for (let hc = 0; hc < this.heatCols; hc++) {
        const val = this.heatGrid[hr * this.heatCols + hc];
        if (val < 0.05) continue;

        const norm = Math.min(val / maxHeat, 1);
        const color = this.heatColor(norm);
        const alpha = 0.15 + norm * 0.4; // 0.15 → 0.55

        gfx.fillStyle(color, alpha);
        gfx.fillRect(hc * cellPx, hr * cellPx, cellPx, cellPx);
      }
    }
  }

  /** Map normalized value (0-1) to a blue→cyan→green→yellow→red gradient */
  private heatColor(t: number): number {
    let r: number, g: number, b: number;
    if (t < 0.25) {
      // Blue → Cyan
      const s = t / 0.25;
      r = 0; g = Math.round(s * 255); b = 255;
    } else if (t < 0.5) {
      // Cyan → Green
      const s = (t - 0.25) / 0.25;
      r = 0; g = 255; b = Math.round((1 - s) * 255);
    } else if (t < 0.75) {
      // Green → Yellow
      const s = (t - 0.5) / 0.25;
      r = Math.round(s * 255); g = 255; b = 0;
    } else {
      // Yellow → Red
      const s = (t - 0.75) / 0.25;
      r = 255; g = Math.round((1 - s) * 255); b = 0;
    }
    return (r << 16) | (g << 8) | b;
  }

  /** Clear all hotspots and cluster state (e.g. between waves) */
  reset(): void {
    this.hotspots.length = 0;
    this.activeClusterIdx = -1;
    this.switchCooldown = 0;
    this.smoothInited = false;
  }

  /** Disconnect event listeners */
  shutdown(): void {
    this.hotspots.length = 0;
    this.heatGfx?.destroy();
    this.heatGfx = null;
    this.heatGrid = [];
    EventBus.off(GameEvents.ENEMY_DESTROYED, this.onEnemyDestroyed);
    EventBus.off(GameEvents.ROBOT_FIRED, this.onRobotFired);
    EventBus.off(GameEvents.ENEMY_FIRED, this.onEnemyFired);
    EventBus.off(GameEvents.BASE_DAMAGED, this.onBaseDamaged);
  }
}
